import { useQueryClient } from '@tanstack/react-query';
import ConversationTopBar from 'components/chats/ConversationTopBar';
import MessageBubble from 'components/chats/MessageBubble';
import MessagesList from 'components/chats/MessagesList';
import MessageInput from 'components/inputs/MessageInput';
import FirstMessageInspiration from 'components/messaging/FirstMessageInspiration';
import { useReferentCaregiver } from 'hooks/useCaregivers';
import { conversationsQueryKey } from 'hooks/useConversations';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { selectCurrentUser } from 'store/users/selectors';
import { isMessagingExplainerV3Enabled } from 'utils/messagingExplainerV3';
import defaultAvatarUrl from '../assets/experts-man-dark.png';

export default function NewConversation() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const redesign = isMessagingExplainerV3Enabled();
  const { referent } = useReferentCaregiver();
  const currentUser = useSelector(selectCurrentUser);
  // The first message shows while it is sent, until the created conversation opens
  const [sendingText, setSendingText] = useState<string | null>(null);
  const sendingMessage: ConversationMessage | null =
    sendingText !== null && currentUser
      ? {
          id: -1,
          message_text: sendingText,
          sender: currentUser,
          sent_at: new Date().toISOString(),
          pending: true,
        }
      : null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [queryStringParams, setQueryStringParams] = useSearchParams();

  // The created conversation takes this page's place, so going back leads to the messaging page
  function openConversation(conversationId: string) {
    // The WebSocket event may have opened it already (see Shell.tsx)
    if (window.location.pathname !== routes.newChat.route) return;
    void queryClient.invalidateQueries({ queryKey: conversationsQueryKey });
    navigate(routes.conversation.route.replace(':conversationId', conversationId), { replace: true });
  }

  function onBackButtonClick() {
    navigate(-1);
  }

  const introMessageText =
    queryStringParams.has('conversationType') && queryStringParams.get('conversationType') === 'checkup'
      ? t('chats.new.introMessageHealthCheckUp')
      : t('chats.new.introMessageAskQuestion');

  const viewTitle =
    queryStringParams.has('conversationType') && queryStringParams.get('conversationType') === 'checkup'
      ? t('chats.new.titleHealthCheckUp')
      : t('chats.new.titleAskQuestion');

  const messages = [
    {
      message_text: introMessageText,
      sender: {
        id: t('chats.new.introMessageSenderId'),
        display_name: t('chats.new.introMessageSenderName'),
        first_name: t('chats.new.introMessageSenderName'),
        is_caregiver: false,
        photo_url: defaultAvatarUrl,
        number_of_conversations_as_patient: 0,
      },
      sent_at: new Date().toISOString(),
    },
  ];

  if (redesign) {
    // An empty conversation with the referring caregiver: who she is, how fast
    // she answers, and help with the first message (Figma 4-Conversation)
    return (
      <div className="flex h-full w-full flex-col items-center bg-background">
        <ConversationTopBar
          showBackButton
          onBackButtonClick={() => onBackButtonClick()}
          caregiver={referent}
          // Her profile opens from the header (not the default referent's: she has none)
          caregiverId={referent.id}
          subtitle={referent.role && t('messagingV3.referentRole', { role: referent.role })}
        />
        {sendingMessage && currentUser ? (
          <div className="flex w-full max-w-content flex-1 flex-col overflow-y-auto py-5">
            <MessageBubble message={sendingMessage} currentUser={currentUser} redesign showFooter />
          </div>
        ) : (
          // Above the composer, as under a caregiver's first messages (Figma « Conversation »)
          <div
            className="flex w-full max-w-content flex-1 flex-col items-center justify-end gap-4 overflow-y-auto px-6 py-4"
            data-testid="conversation-intro"
          >
            <p className="text-center text-base text-muted-foreground">
              {t('messagingV3.conversation.intro', { firstName: referent.firstName })}
            </p>
            <FirstMessageInspiration />
          </div>
        )}
        <div className="w-full max-w-content bg-card">
          <MessageInput
            recipientFirstName={referent.firstName}
            onSending={setSendingText}
            onSendFailed={() => setSendingText(null)}
            onConversationCreated={openConversation}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      <div className="flex h-full w-full flex-col">
        <ConversationTopBar showBackButton name={viewTitle} onBackButtonClick={() => onBackButtonClick()} />
        <MessagesList messages={sendingMessage ? [...messages, sendingMessage] : messages} showExampleQuestions />
        <MessageInput
          onSending={setSendingText}
          onSendFailed={() => setSendingText(null)}
          onConversationCreated={openConversation}
        />
      </div>
    </div>
  );
}
